import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetUsersQuery } from '~/services/api/userManagement';
import Button from '~/components/common/Button';
import { USER_ROLE, USER_ROLE_OPTION } from '~/constants/userManagement';
import { usePaymentInfo } from '~/providers/PaymentProvider';
import { cleanDeep, fromQueryString, toQueryString } from '~/utils/queryString';
import { Box, Stack, Typography } from '@mui/material';
import AddIcon from '~/assets/images/icons/add-white.svg';
import { DEFAULT_PAGE_SIZE } from '~/constants/common';
import { useAuth } from '~/providers/AuthProvider';
import { Pagination } from '~/components/common/Pagination';
import themes from '~/themes';
import { Form, Formik } from 'formik';
import { Field } from '~/components/common/Formik';
import DialogInviteUser from './DialogInviteUser';
import UserTable from './UserTable';

interface IProps {}

export const UserList: React.FC<IProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { account } = useAuth();
  const [openInviteUserDialog, setOpenInviteUserDialog] = useState(false);
  const { isStarterPlan, setIsOpenUpSellDialog } = usePaymentInfo();
  const {
    search,
    sort,
    sort_by,
    page: p,
    role,
  } = fromQueryString(location.search);

  const page = (p || 1) as string;
  const itemPerPage: number = account?.items_per_page || DEFAULT_PAGE_SIZE;

  const { data: rawData, isFetching } = useGetUsersQuery({
    params: {
      sort,
      sort_by,
      search,
      role,
      roles: role
        ? [role]
        : [USER_ROLE.OWNER, USER_ROLE.ADMIN, USER_ROLE.DISPATCHER],
      page,
      limit: itemPerPage,
    },
  });
  const users = useMemo(() => rawData?.items || [], [rawData]);
  const total_pages = useMemo(() => rawData?.total_pages, [rawData]);

  const isEmptyState =
    !rawData?.items?.length && !isFetching && !location.search.length;

  const initialValues = {
    search: search || '',
    role: role || '',
  };

  const onSubmit = useCallback(
    (values: any) => {
      cleanDeep(values, { excludeKeys: ['search'], emptyString: true });
      navigate({
        pathname: location.pathname,
        search: toQueryString({ ...values, page: 1 }),
      });
    },
    [location],
  );

  const filterComponent = useMemo(
    () => (
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ setFieldValue, submitForm }) => (
          <Form>
            <Stack
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: themes.spacing(2.4),
                rowGap: 2,
                columnGap: 0.8,
                [themes.breakpoints.down('md')]: {
                  rowGap: themes.spacing(1),
                  flexWrap: 'wrap',
                  marginBottom: themes.spacing(1.6),
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 0.8,
                  [themes.breakpoints.down('md')]: {
                    width: '100%',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 320,
                    [themes.breakpoints.down('md')]: {
                      width: 'calc(50% - 4px)',
                    },
                  }}
                >
                  <Field.SearchInput
                    name='search'
                    placeholder='Search'
                    onClear={() => {
                      setFieldValue('search', '');
                      submitForm();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        submitForm();
                      }
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    width: 160,
                    [themes.breakpoints.down('md')]: {
                      width: 'calc(50% - 4px)',
                    },
                  }}
                >
                  <Field.Select
                    key='role'
                    placeholder='Role'
                    name='role'
                    options={[
                      {
                        value: USER_ROLE.OWNER,
                        label: 'Owner',
                      },
                      ...USER_ROLE_OPTION,
                    ]}
                    sxPlaceholder={{
                      color: `${themes.color.grayPlaceHolder} !important`,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                    onChange={() => {
                      submitForm();
                    }}
                    onClear={() => {
                      setFieldValue('role', '');
                      submitForm();
                    }}
                  />
                </Box>
              </Box>
              <Button
                onClick={() => {
                  if (isStarterPlan) {
                    return setIsOpenUpSellDialog(true);
                  }
                  return setOpenInviteUserDialog(true);
                }}
              >
                <Typography
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.4,
                  }}
                >
                  <img src={AddIcon} alt='add-icon' />
                  Add User
                </Typography>
              </Button>
            </Stack>
          </Form>
        )}
      </Formik>
    ),
    [search, location, isStarterPlan],
  );

  return (
    <Stack flex={1} width='100%' overflow='hidden'>
      {filterComponent}
      <Stack className='customized-scrollbar' flex={1} overflow='auto'>
        <UserTable isLoading={isFetching} data={users || []} />
      </Stack>
      {!isEmptyState && (
        <Stack sx={{ pt: 2, alignItems: 'flex-end' }}>
          <Pagination
            totalPages={total_pages}
            currentPage={parseInt(page, 10)}
          />
        </Stack>
      )}
      <DialogInviteUser
        open={openInviteUserDialog}
        onClose={() => setOpenInviteUserDialog(false)}
      />
    </Stack>
  );
};
